import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DebtPoint } from '../../services/debt.service';
import { Chart, ChartOptions, ChartData, ChartType } from 'chart.js/auto';

@Component({
  selector: 'app-debt-popup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="debt-modal">
      <div class="debt-modal__header">
        <h2>Deuda externa de Argentina</h2>
        <button type="button" class="close-btn" (click)="activeModal.dismiss()">×</button>
      </div>
      <div class="debt-modal__body" *ngIf="quarterlySeries && quarterlySeries.length; else loading">
        <div class="controls">
          <label for="period">Período:</label>
          <select id="period" [(ngModel)]="periodYears" (change)="updateChart()">
            <option [value]="1">1 año</option>
            <option [value]="3">3 años</option>
            <option [value]="5">5 años</option>
            <option [value]="10">10 años</option>
            <option [value]="20">20 años</option>
          </select>
        </div>
        <div class="summary">
          <div class="summary__item" *ngIf="sourceDate && sourceLabel">
            <span class="label">Último trimestre:</span>
            <span class="value">{{ sourceDate }} ({{ sourceLabel }})</span>
          </div>
          <div class="summary__item" *ngIf="sourceValue !== null">
            <span class="label">Valor trimestral:</span>
            <span class="value">{{ sourceValue | number:'1.0-0' }} M USD</span>
          </div>
          
        </div>
        <div class="chart-wrapper" *ngIf="quarterlySeries && quarterlySeries.length">
          <div class="summary__item" style="margin-bottom:6px;">
            <span class="label">Serie trimestral:</span>
            <span class="value">{{ quarterlySourceLabel || 'INDEC' }}</span>
          </div>
          <canvas #quarterCanvas></canvas>
        </div>

        <div class="note">
          Fuente: INDEC (serie trimestral, M USD).
        </div>
      </div>
      <ng-template #loading>
        <div class="loading">Cargando datos de deuda...</div>
      </ng-template>
    </div>
  `,
  styles: [`
    .debt-modal { padding: 16px; background: #fff; }
    .debt-modal__header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e5e5; padding-bottom: 8px; margin-bottom: 12px; }
    .debt-modal__header h2 { font-size: 18px; margin: 0; color: #1f2937; }
    .close-btn { border: none; background: transparent; font-size: 22px; line-height: 1; cursor: pointer; color: #6b7280; }
    .close-btn:hover { color: #111827; }
    .controls { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
    .controls label { font-size: 13px; color: #6b7280; }
    .controls select { border: 1px solid #e5e7eb; border-radius: 6px; padding: 4px 8px; font-size: 13px; }
    .summary { display: flex; gap: 24px; margin: 8px 0 12px; }
    .summary__item .label { color: #6b7280; margin-right: 6px; font-size: 12px; }
    .summary__item .value { font-weight: 600; color: #111827; }
    .chart-wrapper { width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; background: #fff; }
    canvas { width: 100% !important; height: 320px !important; }
    .tick { font-size: 10px; fill: #666; }
    .note { color: #6b7280; font-size: 12px; margin-top: 8px; }
  `]
})
export class DebtPopupComponent implements AfterViewInit, OnChanges {
  @Input() sourceValue: number | null = null;
  @Input() sourceDate: string | null = null;
  @Input() sourceLabel: string | null = null;
  @Input() quarterlySeries: DebtPoint[] = [];
  @Input() quarterlySourceLabel: string | null = null;
  @ViewChild('quarterCanvas', { static: false }) quarterCanvas!: ElementRef<HTMLCanvasElement>;
  quarterChart: Chart | null = null;
  periodYears = 3;
  lastPoint: DebtPoint | null = null;

  constructor(public activeModal: NgbActiveModal) {}

  ngOnChanges(changes: SimpleChanges) {
    this.updateChart();
  }

  ngAfterViewInit() {
    this.updateChart();
  }

  // Gráfico anual (WB) eliminado; solo se renderiza la serie trimestral de INDEC.

  updateChart() {
    // Actualizar gráfico trimestral si hay datos
    if (this.quarterCanvas && Array.isArray(this.quarterlySeries) && this.quarterlySeries.length) {
      const qPer = this.periodYears * 4;
      const qSeries = this.quarterlySeries.slice(Math.max(0, this.quarterlySeries.length - qPer));
      this.lastPoint = qSeries[qSeries.length - 1] || null;
      const qData: ChartData<'line'> = {
        labels: qSeries.map(s => s.date),
        datasets: [{
          label: `M USD (${this.quarterlySourceLabel || 'INDEC'})`,
          data: qSeries.map(s => s.value),
          borderColor: '#059669',
          backgroundColor: 'rgba(5, 150, 105, 0.15)',
          tension: 0.25,
          fill: true,
          pointRadius: 2
        }]
      };
      const qOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { tooltip: { enabled: true }, legend: { display: true } },
        scales: {
          x: { ticks: { maxRotation: 0, autoSkip: true } },
          y: { ticks: { callback: (v: any) => Intl.NumberFormat().format(Number(v)) } }
        }
      };
      if (this.quarterChart) {
        this.quarterChart.destroy();
        this.quarterChart = null;
      }
      const qctx = this.quarterCanvas.nativeElement.getContext('2d');
      if (qctx) {
        this.quarterChart = new Chart(qctx, { type: 'line', data: qData, options: qOptions });
      }
    }
  }
}